"use client"

import { useState, useEffect, useRef } from "react"
import { usePathname } from "next/navigation"
import Link from "next/link"
import Image from "next/image"
import { Menu, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { CustomButton } from "@/components/custom-button"
import { PromoBanner } from "@sw-business-solutions/cst-mobikart-ui/shared"

const navigation = [
  { name: "News", href: "/news" },
  { name: "Geburtstagsfeier", href: "/geburtstag" },
  { name: "Kartschule", href: "/kartschule" },
  { name: "Preise", href: "/preise" },
  { name: "Slotbörse", href: "/boerse" },
  { name: "Unsere Karts", href: "/karts" },
  { name: "FAQ", href: "/faq" },
  { name: "Bilder", href: "/bilder" },
  { name: "Gutscheine", href: "/gutschein" },
  { name: "Kontakt", href: "/kontakt" },
]

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const pathname = usePathname()
  const headerRef = useRef<HTMLElement>(null)

  // Tatsächliche Headerhöhe (inkl. optionalem Aktions-Banner, das die fixe
  // Leiste höher macht) als CSS-Variable bereitstellen. Seiten ohne Hero
  // setzen ihren oberen Abstand damit dynamisch — ein fester pt-32 schnitt
  // bei aktivem Banner den Inhalt ab.
  useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const update = () =>
      document.documentElement.style.setProperty("--header-h", `${el.offsetHeight}px`)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Menü bei Routenwechsel automatisch schließen
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [pathname])

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true)
      } else {
        setScrolled(false)
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  return (
    <header
      ref={headerRef}
      className={cn(
        "fixed w-full z-40 transition-all duration-300",
        scrolled ? "bg-white shadow-md" : "bg-[#d26e19]",
      )}
    >
      {/* Aktions-Banner als Streifen der fixierten Leiste. Rendert sich selbst
          nur, wenn gerade eines gilt — sonst faellt es auf null zurueck. */}
      <PromoBanner />
      <nav className="container-custom flex items-center justify-between py-3">
        <div className="flex 2xl:flex-1 relative z-50">
          <Link href="/" className="-m-1.5 p-1.5">
            <span className="sr-only">MOBIKART</span>
            <div className="relative">
              <div
                className={cn(
                  "absolute -bottom-6 -left-6 -right-6 -top-6 bg-black rounded-none rounded-b-lg transform transition-all duration-300",
                  scrolled ? "rounded-b-lg" : "rounded-b-xl",
                )}
              ></div>
              <div className="relative px-1 py-1">
                <Image src="/logo.svg" alt="MOBIKART Logo" height={300} width={520} className="h-20 w-auto" />
              </div>
            </div>
          </Link>
        </div>

        <div className="flex 2xl:hidden relative z-50">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Menü öffnen</span>
            <Menu className={cn("h-6 w-6", scrolled ? "text-gray-700" : "text-white")} aria-hidden="true" />
          </button>
        </div>

        <div className="hidden 2xl:flex 2xl:gap-x-5">
          {navigation.map((item) => (
            <Link
              key={item.name}
              href={item.href}
              className={cn(
                "text-sm font-semibold leading-6 transition-colors whitespace-nowrap",
                scrolled ? "text-gray-900 hover:text-[#d26e19]" : "text-white hover:text-gray-200",
              )}
            >
              {item.name}
            </Link>
          ))}
        </div>

        <div className="hidden 2xl:flex 2xl:flex-1 2xl:justify-end 2xl:items-center 2xl:gap-3">
          <a
            href="https://www.hps-pitbike.de/buchen"
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors whitespace-nowrap text-base px-4 py-2 border-2",
              scrolled
                ? "border-[#009aab] text-[#009aab] hover:bg-[#009aab] hover:text-white"
                : "bg-white/20 text-white border-white hover:bg-white hover:text-black",
            )}
          >
            🏍️ Motorrad statt Kart?
          </a>
          <CustomButton href="/buchen" variant={scrolled ? "primary" : "white"}>
            Buchen
          </CustomButton>
        </div>
      </nav>

      {/* Mobile menu */}
      <div className={`2xl:hidden ${mobileMenuOpen ? "fixed inset-0 z-50" : "hidden"}`}>
        <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <Link href="/" className="-m-1.5 p-1.5" onClick={() => setMobileMenuOpen(false)}>
              <span className="sr-only">MOBIKART</span>
              <div className="relative">
                <div className="absolute -bottom-6 -left-6 -right-6 -top-6 bg-black rounded-none rounded-b-lg"></div>
                <div className="relative px-1 py-1">
                  <Image src="/logo.svg" alt="MOBIKART Logo" height={280} width={480} className="h-16 w-auto" />
                </div>
              </div>
            </Link>
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 text-gray-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Menü schließen</span>
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    href={item.href}
                    className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
              <div className="py-6 space-y-3">
                <CustomButton
                  href="/buchen"
                  variant="primary"
                  className="w-full"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Buchen
                </CustomButton>
                <a
                  href="https://www.hps-pitbike.de/buchen"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full rounded-lg border-2 border-[#009aab] px-4 py-2.5 text-sm font-bold text-[#009aab] hover:bg-[#009aab] hover:text-white transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  🏍️ Lieber Motorrad fahren? → HPS Pitbike
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
